import React, { ReactElement } from "react"
import _ from "lodash"
import property from "data/property"
import validationService from "data/validate"
import toggles from 'feature-toggles'

// Validation error type structure:
type PropertyValidationErrors = {
  [DBTableName: string]: {
    [DBTableRowId: number]: ValidationErrorObject[]
  }
}
type ValidationErrorObject = {
  path: string[] // DB json path, e.g. ['rentdetail','items',3,'renewal']. current input/cell filters on path to find its errors for highlighting
  type: string   // e.g. 'object.allowUnknown', allows for filtering out some error types if they're tagged as internal
  tags: string[] // e.g. 'object.allowUnknown:internal'
}

// Types for these helper functions
type BodyFn = (rowData: any, props: any) => string | ReactElement<any>
type ErrorPredicate = (error: ValidationErrorObject, rowData?: any) => boolean

/**
 * field : (table: string) => (field: string, body?: BodyFn, isError?: ErrorPredicate) => ({field: string, body: BodyFn})
 * Generate field and body props for PrimeReact Column component with injected data attributes for focus automation (including validation highlighting).
 *
 * @param table: current database table.  curried to reduce redundancy in Column, e.g.
 *      import { field as _field } from "./column-field"
 *      const field = _field('lineitem')
 *      ...
 *      <Column {...field('code')} header="Code" sortable={true} style={{minWidth: "150px"}} />
 *
 * @param field: path to db field, e.g. 'unitinventorydetail.unitsTotal'
 * @param body?: text/component to wrap in span in table.
 * @param isError?: callback for custom error recognition when the `${prefix}.${field}` path isn't enough to filter the errors.
 * @returns {field: string, body: BodyFn} for PrimeReact API
 */

export const field = (table: string) => (field: string, body?: BodyFn|null, isError?:ErrorPredicate, idField?:string, dragFn?:Function) => {
  return {
    field,
    body: (rowData: object, props: object) => {
      let content = body ? body(rowData, props) : _.get(rowData, field)
      let dattrs = field_attrs(table)(field, isError, idField)(rowData, props)
      return (
        <span {...dattrs} draggable={dragFn ? true : false} onDragStart={(e) => dragFn && dragFn(e, rowData)}>
          {content}
        </span>
      )
    }
  }
}

/**
 * field_attrs
 * Factored out attribute injection helper from `field` method for reuse in nested/non-PrimeReact controls.
 * Passes a callback to individual controls so they can pass their specific path to filter errors correctly.
 *
 * Adds `prefix` to the field/isError parameters above.
 *
 * @param prefix  for building nested paths when callback field parameter is not from column top-level,
 *    e.g. for detailed items
 *
 *      const attrs = (field) => field_attrs('area', 'areadetail')(field)(props.rowData, props)  // e.g. callback field='floor'
 *      const item_attrs = (field) => field_attrs('area', 'areadetail.items')(field)(props.rowData, props)  // e.g. callback field=[0, 'percent']
 *
 *    versus usage from _field method at table level where the control will pass the column name as the field parameter.
 *
 * @returns
 */
export const field_attrs = (table: string, prefix: string = '' ) => (field: string|string[], isError?: Function, idField?:string) => (rowData, props) => {
  const content = _.get(rowData, field)
  const id = _.get(rowData, idField || (table + 'id'))
  field = _.isArray(field) ? _.join(field, '.') : field
  const path = prefix ? `${prefix}.${field}` : field
  // NB e.path might be to the outer object, so the e.context.path might provide the next deeper key to isolate the error to a single input
  //   e.g. when e.path is ["recoverygroupdetail","items",1] for a dupe item, e.context.path is "recoveryNamedItemId"
  const onPath = (e) => e.path.join('.').startsWith(path) || e.path.concat(e.context.path).join('.').startsWith(path)
  const filter = (e) => onPath(e) || isError && isError(e, rowData)

  const newValidationTables = [
    'asset',
    'assetattribute',
    'attribute',
    'chart',
    'cashflowmap',
    'detailaccount',
    'folder',
    'organization',
    'portfolio',
    'summaryaccount',
    'usergroup',
    'userrole'
  ]

  let errors: ValidationErrorObject[]
  if (toggles.validationRefactor() || newValidationTables.includes(table)) {
    const _errors = validationService.value()
    errors = _.get(_errors, ['errordetail', table, id])
  } else {
    const _errors = property.get()
    errors = _.get(_errors, ['errordetail', table, id])
  }
  errors = _.filter(errors, filter)
  errors = _.filter(errors, (e) => !_.some(e.tags, t => t == `${e.type}:internal`))

  let dattrs = {
    'data-id': id,
    'data-field': field,  // used by styles
    'data-path': path
  }
  if (typeof content == 'string') dattrs['data-content'] = content
  if (errors.length)              dattrs['data-error'] = true  // used by table-automation::updateValidation
  return dattrs
}

// NB the table-automation updateValidation helper
// - works by finding all data-error=true DOM elements and adding .has-errors to them for styling.
// - is mostly called automatically (e.g. on cell selection, property channel updates) but must be called manually in some edge cases